// CST207: public entry point for the i18n module.
export { initI18n, i18n, DEFAULT_LNG, SUPPORTED_LNGS } from "./config";
export { NAMESPACES, type Namespace } from "./namespaces";
export { resources } from "./resources";

import { API_BASE_URL } from "@/lib/api-base";

const I18N_API_BASE = API_BASE_URL;

export type OverridesPayload = Record<
  string,
  Record<string, Record<string, string>>
>;

/**
 * Convert a flat dotted-key map into a nested object that i18next can resolve.
 * e.g. {"sidebar.newSearch": "v"} → {"sidebar": {"newSearch": "v"}}
 *
 * i18next's `t()` navigates nested objects using the keySeparator (default "."),
 * but `addResourceBundle` stores keys literally. Without unflattening, overrides
 * with dotted keys would never be resolved by `t()`.
 */
export function unflattenKeys(
  flat: Record<string, string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [dotKey, value] of Object.entries(flat)) {
    const parts = dotKey.split(".");
    let current: Record<string, unknown> = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in current) || typeof current[parts[i]] !== "object") {
        current[parts[i]] = {};
      }
      current = current[parts[i]] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = value;
  }
  return result;
}

/**
 * Fetch translation overrides from the backend and merge into i18next before
 * first render. Failures are non-fatal — the app falls back to bundled
 * translations. Used at boot from main.tsx; NOT wrapped in React Query because
 * QueryClientProvider isn't mounted yet (ADR-2).
 */
export async function fetchAndApplyOverrides(
  i18nInstance: typeof import("i18next").default,
): Promise<OverridesPayload | null> {
  try {
    const res = await fetch(`${I18N_API_BASE}/api/i18n/overrides`);
    if (!res.ok) return null;
    const overrides = (await res.json()) as OverridesPayload | null;
    if (!overrides || typeof overrides !== "object") return null;
    for (const [lng, nsMap] of Object.entries(overrides)) {
      if (!nsMap || typeof nsMap !== "object") continue;
      for (const [ns, data] of Object.entries(nsMap)) {
        if (!data || typeof data !== "object") continue;
        // Unflatten dotted keys so i18next's keySeparator-based
        // resolution can find them (e.g. "sidebar.newSearch" → nested).
        const nested = unflattenKeys(data);
        i18nInstance.addResourceBundle(
          lng,
          ns,
          nested,
          /* deep */ true,
          /* overwrite */ true,
        );
      }
    }
    return overrides;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[i18n] override fetch failed, using bundled resources", e);
    return null;
  }
}
